import React from 'react';
import { imageUrl } from '../../utils';

interface Technology {
  name: string;
  description: string;
  logo?: string;
}

interface TechnologyStackProps {
  title?: string;
  technologies: Technology[];
  securityTitle?: string;
  securityDescription?: string;
  backgroundImage?: string;
  imagePosition?: 'left' | 'right';
  imageLayout?: 'row' | 'offset';
}

const TechnologyStack = ({ 
  title = 'Technology Stack',
  technologies,
  securityTitle = 'Security & Compliance',
  securityDescription = 'HIPAA compliant infrastructure with end-to-end encryption, SOC 2 Type II certified, regular security audits, and 99.99% uptime SLA.',
  backgroundImage,
  imagePosition = 'right',
  imageLayout = 'offset'
}: TechnologyStackProps) => {
  return (
    <section className="bg-muted/30 py-16 overflow-hidden">
      <div className="container mx-auto px-4">
        {backgroundImage ? (
          // Side-by-side layout when image is provided
          <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-start">
            {/* Content - order changes based on imagePosition */}
            <div className={imagePosition === 'left' ? 'order-2' : 'order-1'}>
              <h2 className="text-4xl font-bold text-foreground text-center mb-12">
                {title}
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {technologies.map((tech, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-4 p-6 rounded-xl bg-card border border-border"
                  >
                    {tech.logo ? (
                      <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center">
                        <img
                          src={imageUrl(tech.logo)}
                          alt={tech.name}
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                    ) : (
                      <div className="w-3 h-3 rounded-full bg-gradient-to-r from-accent to-primary flex-shrink-0"></div>
                    )}
                    <div className="flex-1">
                      <div className="font-bold text-foreground">{tech.name}</div>
                      <div className="text-sm text-[#728F8B]">{tech.description}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-8 p-6 rounded-xl bg-card border border-accent/30 flex items-start gap-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-shield w-6 h-6 text-[#FA832E] flex-shrink-0 mt-1"
                >
                  <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"></path>
                </svg>
                <div>
                  <div className="font-bold text-foreground mb-2">{securityTitle}</div>
                  <p className="text-[#728F8B] text-sm">{securityDescription}</p>
                </div>
              </div>
            </div>

            {/* Images stacked - order changes based on imagePosition */}
            <div className={`hidden md:flex items-center justify-center relative overflow-visible ${imagePosition === 'left' ? 'order-1' : 'order-2'}`} style={{ minHeight: '600px' }}>
              {/* Background image - centered */}
              {backgroundImage === 'scholastic-image2.png' && (
                <div className="absolute" style={{ zIndex: 0 }}>
                  <img
                    src={imageUrl('scholastic-onboarding.png')}
                    alt=""
                    className="max-w-full max-h-[600px] object-contain"
                  />
                </div>
              )}
              {backgroundImage === 'realty-pass/Homepage.png' && (
                <div className="absolute" style={{ zIndex: 0 }}>
                  <img
                    src={imageUrl('realty-pass/Homepage.png')}
                    alt=""
                    className="max-w-full max-h-[600px] object-contain"
                  />
                </div>
              )}
              {backgroundImage === 'smart-certs/main.png' && (
                <>
                  {imageLayout === 'row' ? (
                    <div className="flex gap-4 items-center justify-center">
                      <img
                        src={imageUrl('smart-certs/image-2.png')}
                        alt=""
                        className="max-w-full max-h-[500px] object-contain rounded-lg"
                      />
                      <img
                        src={imageUrl('smart-certs/image-3.png')}
                        alt=""
                        className="max-w-full max-h-[500px] object-contain rounded-lg"
                      />
                    </div>
                  ) : (
                    <>
                      <div className="absolute" style={{ zIndex: 0 }}>
                        <img
                          src={imageUrl('smart-certs/image-2.png')}
                          alt=""
                          className="max-w-full max-h-[500px] object-contain rounded-lg"
                        />
                      </div>
                      <div className={`relative top-32 ${imagePosition === 'left' ? '-translate-x-1/4' : 'translate-x-1/4'}`} style={{ zIndex: 1 }}>
                        <img
                          src={imageUrl('smart-certs/image-3.png')}
                          alt=""
                          className="max-w-full max-h-[500px] object-contain rounded-lg"
                        />
                      </div>
                    </>
                  )}
                </>
              )}
              {backgroundImage === 'stylematch/images.png' && (
                <div className="flex gap-4 items-center justify-center">
                  <img
                    src={imageUrl('stylematch/image-4.png')}
                    alt=""
                    className="max-w-full max-h-[500px] object-contain rounded-lg"
                  />
                  <img
                    src={imageUrl('stylematch/image-5.png')}
                    alt=""
                    className="max-w-full max-h-[500px] object-contain rounded-lg"
                  />
                </div>
              )}
              {/* Foreground image - offset above and halfway across, adjust direction based on position */}
              {backgroundImage !== 'smart-certs/main.png' && backgroundImage !== 'stylematch/images.png' && (
                <div className={`relative -top-16 ${imagePosition === 'left' ? '-translate-x-1/4' : 'translate-x-1/4'}`} style={{ zIndex: 1 }}>
                  {backgroundImage === 'realty-pass/Homepage.png' ? (
                    <img
                      src={imageUrl('realty-pass/checklist_manager.png')}
                      alt=""
                      className="max-w-full max-h-[600px] object-contain"
                    />
                  ) : (
                    <img
                      src={imageUrl(backgroundImage)}
                      alt=""
                      className="max-w-full max-h-[600px] object-contain"
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          // Original centered layout when no image
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-foreground text-center mb-12">
              {title}
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {technologies.map((tech, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 p-6 rounded-xl bg-card border border-border"
                >
                  {tech.logo ? (
                    <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center">
                      <img
                        src={imageUrl(tech.logo)}
                        alt={tech.name}
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                  ) : (
                    <div className="w-3 h-3 rounded-full bg-gradient-to-r from-accent to-primary flex-shrink-0"></div>
                  )}
                  <div className="flex-1">
                    <div className="font-bold text-foreground">{tech.name}</div>
                    <div className="text-sm text-[#728F8B]">{tech.description}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8 p-6 rounded-xl bg-card border border-accent/30 flex items-start gap-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-shield w-6 h-6 text-[#FA832E] flex-shrink-0 mt-1"
              >
                <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"></path>
              </svg>
              <div>
                <div className="font-bold text-foreground mb-2">{securityTitle}</div>
                <p className="text-[#728F8B] text-sm">{securityDescription}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default TechnologyStack;

